import { useMutation, useQueryClient } from "@tanstack/react-query";
import { signOut } from "aws-amplify/auth";
import { useSWRConfig } from "swr";
import api from "~/shared/api/core/api";
import { PARTICIPANTS_KEYS } from "~/shared/api/participants/participants.queries";
import { USERS_QUERIES } from "~/shared/api/users/users.queries";
import { UserProfileResponse } from "~/shared/api/users/users.types";
import { Theme } from "~/shared/components/UserThemePreference";

export function useLogout() {
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();

  return useMutation({
    mutationFn: async () => {
      await signOut();
    },
    onSuccess: () => {
      queryClient.clear();
      mutate(() => true, undefined, { revalidate: false });
    },
  });
}

export function useActivateUserAccount(id: string | number) {
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();

  return useMutation<unknown, Error, Record<string, unknown>>({
    mutationFn: async (data) => {
      const response = await api.post(`users/${id}/activate`, {
        ...data,
        // We always want to send a formal welcome email when activating a user account
        formalWelcome: true,
      });

      return response.data;
    },
    onSuccess: () => {
      mutate(checkParticipantKey);
      queryClient.invalidateQueries({
        queryKey: PARTICIPANTS_KEYS.detail(id.toString()),
      });
    },
  });
}

export function useReactivateParticipantAccount(id: string | number) {
  const { mutate } = useSWRConfig();

  return useMutation<unknown, Error, Record<string, unknown>>({
    mutationFn: async (data) => {
      const response = await api.post(`users/${id}/activate`, {
        ...data,
        // We don't want to send a formal welcome email when reactivating a user account
        formalWelcome: false,
      });

      return response.data;
    },
    onSuccess: () => {
      mutate(checkParticipantKey);
    },
  });
}

export function useDeactivateAccount(id: number) {
  const { mutate } = useSWRConfig();

  return useMutation({
    mutationFn: async () => {
      await api.post(`/users/${id}/deactivate`, {});
    },
    onSuccess: () => {
      mutate(checkParticipantKey);
    },
  });
}

function checkParticipantKey(args: string | [string, unknown]) {
  return typeof args === "string" && args.startsWith("participants/");
}

const THEME_PREF_MUTATION_KEY = ["update-theme-preference"] as const;

export function useUpdateThemePreference(userId: number) {
  const queryClient = useQueryClient();
  const queryKey = USERS_QUERIES.profile().queryKey;

  return useMutation({
    mutationKey: THEME_PREF_MUTATION_KEY,
    mutationFn: async ({ theme }: { theme: Theme }) => {
      const response = await api.patch<UserProfileResponse>(`/users/${userId}`, {
        applicationTheme: theme,
      });

      return response.data;
    },
    onMutate: async ({ theme }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousUser = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return;

        return {
          ...oldData,
          applicationTheme: theme,
        };
      });

      return { previousUser };
    },
    onError: (_err, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousUser);
    },
    onSettled: () => {
      // isMutating returns the number of active mutations for the given mutation key
      // If there is only one active mutation, we can invalidate the query
      if (queryClient.isMutating({ mutationKey: THEME_PREF_MUTATION_KEY }) === 1) {
        queryClient.invalidateQueries({
          queryKey,
        });
      }
    },
  });
}
